import { useAnalytics } from '@backstage/core-plugin-api';

import {
  extractLastNameComponent,
  invariant,
} from '@spotify/confidence-core-react';
import {
  CollaborationV1AnchorInput,
  CollaborationV1CommentReplyAction,
  CommentFragment,
  CommentFragmentDoc,
  CommentRepliesDocument,
  CommentRepliesQuery,
  CommentsDocument,
  CommentsQuery,
  IdentityFragment,
  getError,
  getTypeOrNull,
  isError,
  useAddCollaborationCommentMutation,
  useAddCollaborationReplyMutation,
  useDeleteCollaborationCommentMutation,
} from '@spotify/confidence-graphql';
import { useCurrentUser } from '@spotify/plugin-confidence-iam-react';

import { type Anchor } from '../../types';
import { reactionSummaryNameFromResource } from '../../utils';
import { updateCommentRepliesQuery } from '../useCommentReplies/useCommentReplies';
import { updateCommentsQuery } from '../useComments/useComments';

const generateRandomCommentId = (workflowInstance: string) => {
  const lastComponent = extractLastNameComponent(workflowInstance);
  return `${lastComponent}-${Math.random().toString(36).substring(7)}`;
};

const toAnchor = (anchor: Anchor): CollaborationV1AnchorInput => ({
  zone: { zone: anchor.zone },
  document: null,
});

export const useCommentApi = (resource: string) => {
  const { currentUser } = useCurrentUser();
  const analytics = useAnalytics();

  // The Identity we use for optimistic updates
  const author = getTypeOrNull(currentUser?.identity) || undefined;

  const [addCommentMutation] = useAddCollaborationCommentMutation({});
  const [addReplyMutation] = useAddCollaborationReplyMutation();
  const [deleteCommentMutation] = useDeleteCollaborationCommentMutation();

  const addComment = async (
    content: string,
    anchor?: Anchor,
    assignee?: IdentityFragment | null,
  ) => {
    invariant(author, 'must have an author to add a comment');

    const commentId = generateRandomCommentId(resource);
    const response = await addCommentMutation({
      variables: {
        commentId,
        comment: {
          resource,
          content,
          anchor: anchor ? toAnchor(anchor) : null,
          assignee: assignee?.name,
        },
      },
      onCompleted: data => {
        if (isError(data?.createComment)) {
          throw getError(data?.createComment);
        }
      },
      optimisticResponse: {
        createComment: {
          __typename: 'CollaborationV1Comment',
          name: `comments/${commentId}`,
          content,
          resource: resource,
          resolved: false,
          anchor: anchor ? toAnchor(anchor) : null,
          assignee: assignee
            ? {
                // Apollo cache requires us to include all fields
                // in the assignee object, even if they are null.
                user: assignee?.user ?? null,
                apiClient: null,
                group: null,
                everyone: null,
                ...assignee,
              }
            : null,
          author,
          createTime: new Date().toISOString(),
          updateTime: new Date().toISOString(),
          queryReactionSummary: {
            __typename: 'CollaborationV1QueryReactionSummaryResponse',
            summary: {
              name: reactionSummaryNameFromResource(`comments/${commentId}`),
              commentCount: 0,
              emojiReactions: [],
            },
          },
        },
      },
      update: (cache, { data: result }) => {
        const comment = getTypeOrNull(result?.createComment);

        cache.updateQuery<CommentsQuery>(
          {
            query: CommentsDocument,
            variables: {
              resource,
              filter: null,
            },
          },
          data => updateCommentsQuery(data, comment!),
        );
      },
    });

    if (response.errors) {
      throw response.errors[0];
    }

    const commentName = getTypeOrNull(response?.data?.createComment)?.name;

    analytics.captureEvent('add-comment', resource, {
      value: content.length,
      attributes: {
        zone: anchor?.zone ?? 'unknown',
        assign: Boolean(assignee),
        commentName: commentName ?? '',
      },
    });

    return commentName;
  };

  const deleteComment = async (commentName: string) => {
    analytics.captureEvent('delete-comment', resource, {
      attributes: { commentName },
    });

    await deleteCommentMutation({
      variables: {
        commentName,
      },
      optimisticResponse: {
        deleteComment: {
          __typename: 'CollaborationV1Comment',
        },
      },
      update: cache => {
        cache.modify({
          fields: {
            comments(existing = { comments: [] }, { storeFieldName }) {
              if (storeFieldName.includes(resource)) {
                return {
                  ...existing,
                  comments: existing.comments.filter(
                    (c: any) => c.name !== commentName,
                  ),
                };
              }

              return existing;
            },
          },
        });
      },
    });
  };

  const addReply = async (
    commentName: string,
    content: string,
    assignee?: IdentityFragment | null,
  ): Promise<string | undefined> => {
    invariant(author, 'must have an author to add a comment');
    const commentReplyId = generateRandomCommentId(resource);

    const response = await addReplyMutation({
      variables: {
        commentName,
        commentReplyId,
        reply: {
          content,
          assignee: assignee?.name,
          action: assignee
            ? CollaborationV1CommentReplyAction.Assign
            : undefined,
        },
      },
      optimisticResponse: {
        createCommentReply: {
          __typename: 'CollaborationV1CommentReply',
          name: `${commentName}/replies/${commentReplyId}`,
          content,
          author,
          assignee: assignee
            ? {
                // Apollo cache requires us to include all fields
                // in the assignee object, even if they are null.
                user: assignee?.user ?? null,
                apiClient: null,
                group: null,
                everyone: null,
                ...assignee,
              }
            : null,
          action: assignee ? CollaborationV1CommentReplyAction.Assign : null,
          createTime: new Date().toISOString(),
          updateTime: new Date().toISOString(),
          queryReactionSummary: {
            __typename: 'CollaborationV1QueryReactionSummaryResponse',
            summary: {
              name: reactionSummaryNameFromResource(
                `${commentName}/replies/${commentReplyId}`,
              ),
              commentCount: 0,
              emojiReactions: [],
            },
          },
        },
      },
      onCompleted: data => {
        if (isError(data?.createCommentReply)) {
          throw getError(data?.createCommentReply);
        }
      },
      update: (cache, { data: result }) => {
        const commentReply = getTypeOrNull(result?.createCommentReply);

        cache.updateFragment<CommentFragment>(
          {
            fragment: CommentFragmentDoc,
            fragmentName: 'Comment',
            id: cache.identify({
              __typename: 'CollaborationV1Comment',
              name: commentName,
            }),
          },
          existing => {
            if (!existing) return null;

            return {
              ...existing,
              assignee: {
                // Apollo cache requires us to include all fields
                // in the assignee object, even if they are null.
                user: null,
                apiClient: null,
                group: null,
                everyone: null,
                ...assignee,
              },
            };
          },
        );

        cache.updateQuery<CommentRepliesQuery>(
          {
            query: CommentRepliesDocument,
            variables: {
              parent: commentName,
              pageSize: 5,
            },
          },
          data => updateCommentRepliesQuery(data, commentReply!),
        );
      },
    });

    if (response.errors) {
      throw response.errors[0];
    }

    analytics.captureEvent('add-reply', resource, {
      value: content.length,
      attributes: {
        assign: Boolean(assignee),
        commentReplyId,
        inReplyTo: commentName,
      },
    });

    return commentReplyId;
  };

  const resolveComment = async (commentName: string) => {
    invariant(author, 'must have an author to add a comment');
    const commentReplyId = generateRandomCommentId(resource);

    analytics.captureEvent('resolve-thread', resource, {
      attributes: { commentId: commentName },
    });

    await addReplyMutation({
      variables: {
        commentName,
        commentReplyId,
        reply: {
          action: CollaborationV1CommentReplyAction.Resolve,
          content: '',
        },
      },
      optimisticResponse: {
        createCommentReply: {
          __typename: 'CollaborationV1CommentReply',
          name: `${commentName}/replies/${commentReplyId}`,
          content: '',
          author,
          assignee: null,
          action: CollaborationV1CommentReplyAction.Resolve,
          createTime: new Date().toISOString(),
          updateTime: new Date().toISOString(),
          queryReactionSummary: {
            __typename: 'CollaborationV1QueryReactionSummaryResponse',
            summary: {
              name: reactionSummaryNameFromResource(
                `${commentName}/replies/${commentReplyId}`,
              ),
              commentCount: 0,
              emojiReactions: [],
            },
          },
        },
      },
      onCompleted: data => {
        if (isError(data?.createCommentReply)) {
          throw getError(data?.createCommentReply);
        }
      },
      update: (cache, { data: result }) => {
        const commentReply = getTypeOrNull(result?.createCommentReply);

        if (!commentReply) return;

        cache.updateFragment<CommentFragment>(
          {
            fragment: CommentFragmentDoc,
            fragmentName: 'Comment',
            id: cache.identify({
              __typename: 'CollaborationV1Comment',
              name: commentName,
            }),
          },
          existing => {
            if (!existing) return undefined;

            return {
              ...existing,
              resolved: true,
            };
          },
        );

        cache.updateQuery<CommentRepliesQuery>(
          {
            query: CommentRepliesDocument,
            variables: {
              parent: commentName,
              pageSize: 5,
            },
          },
          data => updateCommentRepliesQuery(data, commentReply!),
        );
      },
    });
  };

  const reopenComment = async (commentName: string) => {
    invariant(author, 'must have an author to add a comment');
    const commentReplyId = generateRandomCommentId(resource);

    analytics.captureEvent('reopen-thread', resource, {
      attributes: { commentId: commentName },
    });

    await addReplyMutation({
      variables: {
        commentName,
        commentReplyId,
        reply: {
          action: CollaborationV1CommentReplyAction.Reopen,
          content: '',
        },
      },
      optimisticResponse: {
        createCommentReply: {
          __typename: 'CollaborationV1CommentReply',
          name: `${commentName}/replies/${commentReplyId}`,
          content: '',
          author,
          assignee: null,
          action: CollaborationV1CommentReplyAction.Reopen,
          createTime: new Date().toISOString(),
          updateTime: new Date().toISOString(),
          queryReactionSummary: {
            __typename: 'CollaborationV1QueryReactionSummaryResponse',
            summary: {
              name: reactionSummaryNameFromResource(
                `${commentName}/replies/${commentReplyId}`,
              ),
              commentCount: 0,
              emojiReactions: [],
            },
          },
        },
      },
      onCompleted: data => {
        if (isError(data?.createCommentReply)) {
          throw getError(data?.createCommentReply);
        }
      },
      update: (cache, { data: result }) => {
        const commentReply = getTypeOrNull(result?.createCommentReply);

        cache.updateFragment<CommentFragment>(
          {
            fragment: CommentFragmentDoc,
            fragmentName: 'Comment',
            id: cache.identify({
              __typename: 'CollaborationV1Comment',
              name: commentName,
            }),
          },
          existing => {
            if (!existing) return undefined;

            return {
              ...existing,
              resolved: false,
            };
          },
        );

        cache.updateQuery<CommentRepliesQuery>(
          {
            query: CommentRepliesDocument,
            variables: {
              parent: commentName,
              pageSize: 5,
            },
          },
          data => updateCommentRepliesQuery(data, commentReply!),
        );
      },
    });
  };

  return {
    addComment,
    deleteComment,
    addReply,
    resolveComment,
    reopenComment,
  };
};
